// src/hooks/usePOSProducts.js
import { useState, useEffect, useCallback } from "react";
import { getPOSProducts } from "../services/posProductServices";
import { authStorage } from "../utils/authStorage";

const uploadsBaseURL = process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/uploads`
    : "";

const normalizeImageUrl = (image) => {
    if (!image) return null;

    if (/^https?:\/\//i.test(image)) {
        try {
            const url = new URL(image);
            const uploadsIndex = url.pathname.indexOf("/uploads/");
            if (uploadsIndex !== -1 && uploadsBaseURL) {
                const relativePath = url.pathname.slice(uploadsIndex).replace(/^\/uploads/, "");
                return `${uploadsBaseURL}${relativePath}`;
            }
            return image;
        } catch (e) {
            return image;
        }
    }

    if (image.startsWith("/uploads/") || image.startsWith("uploads/")) {
        const relativePath = image.replace(/^\/?uploads\//, "");
        return uploadsBaseURL ? `${uploadsBaseURL}/${relativePath}` : image;
    }

    return image;
};

export const usePOSProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch products from backend
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // /api/pos/products already returns products with stock from inventory JOIN
            const user = authStorage.getUser();
            const branchId = user?.branch_id;
            const productsRes = await getPOSProducts(branchId);
            // Defensive: filter out deleted products if any slip through
            const filteredProducts = Array.isArray(productsRes) ? productsRes.filter(p => !p.status || p.status !== "DELETED") : [];
            const normalizedProducts = filteredProducts.map((product) => ({
                ...product,
                image: normalizeImageUrl(product.image),
            }));
            setProducts(normalizedProducts);
        } catch (err) {
            console.error("Error fetching POS products:", err);
            setError(err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Decrement stock after order
    const updateStockAfterOrder = useCallback((orderedItems) => {
        setProducts(prev =>
            prev.map(prod => {
                const item = orderedItems.find(i => i.product_id === prod.id);
                if (item) return { ...prod, stock: prod.stock - item.qty };
                return prod;
            })
        );
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    return { products, loading, error, fetchProducts, setProducts, updateStockAfterOrder };
};
